import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { settingsApi } from '@/features/settings/api';

const STYLE_TAG_ID = 'pp-org-custom-css';

/**
 * Applies Settings > Themes > Custom CSS org-wide. Deliberately scoped to
 * exactly this — Primary/Accent Color and Font Family (also stored on
 * theme_settings) are surfaced in ThemeSettingsPage as a saved preview
 * rather than live-reskinning the app: Tailwind's palette here is
 * compiled to static hex values, not CSS custom properties, so an
 * honest runtime "brand-600 everywhere" override would mean unreliable
 * `!important` rules fighting dark-mode variants across hundreds of
 * utility classes — a real Tailwind config change (CSS-var-driven
 * colors), not a Settings page feature. Raw Custom CSS has no such
 * problem; it's just a stylesheet, so it's fully live here.
 */
export function OrgCustomCssInjector() {
  const { data: theme } = useQuery({ queryKey: ['settings', 'theme'], queryFn: settingsApi.getTheme, staleTime: 5 * 60 * 1000 });

  useEffect(() => {
    let styleTag = document.getElementById(STYLE_TAG_ID) as HTMLStyleElement | null;
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = STYLE_TAG_ID;
      document.head.appendChild(styleTag);
    }
    styleTag.textContent = theme?.custom_css ?? '';
  }, [theme?.custom_css]);

  return null;
}
